/**
 * DriveZone — Register Page
 * Route: /register
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader2, CheckCircle, Gift } from 'lucide-react'
import api from '@/lib/api'
import { useAuthStore, type AuthUser } from '@/stores/authStore'
import { AxiosError } from 'axios'

interface RegisterResponse {
  message: string
  user?: AuthUser
}

export default function RegisterPage() {
  const navigate = useNavigate()
  const { setUser } = useAuthStore()
  const [searchParams] = useSearchParams()
  const refCode = searchParams.get('ref')?.trim().toUpperCase() ?? ''

  const [firstName, setFirstName] = useState('')
  const [email, setEmail]         = useState('')
  const [password, setPassword]   = useState('')
  const [confirm, setConfirm]     = useState('')
  const [showPw, setShowPw]       = useState(false)
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState('')
  const [success, setSuccess]     = useState(false)

  const passwordStrong = password.length >= 8 && /\d/.test(password)
  const passwordsMatch = password === confirm && confirm.length > 0

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')

    if (!firstName.trim()) {
      setError('Please enter your first name.')
      return
    }
    if (!passwordStrong) {
      setError('Password must be at least 8 characters and contain a number.')
      return
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      const res = await api.post<RegisterResponse>('/api/auth/register/', {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        password,
        ...(refCode ? { referral_code: refCode } : {}),
      })

      if (res.data.user) {
        // SKIP_OTP_MODE: backend created session, go straight to app
        setUser(res.data.user)
        navigate('/app/search', { replace: true })
      } else {
        setSuccess(true)
        setTimeout(() => navigate('/login'), 2500)
      }
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: string }>
      if (axiosErr.response?.status === 409) {
        const errorCode = axiosErr.response.data?.error
        if (errorCode === 'ip_exists') {
          setError('An account has already been registered from this device.')
        } else {
          setError('An account with this email already exists.')
        }
      } else {
        setError(axiosErr.response?.data?.message ?? 'Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center space-y-4 w-full max-w-sm">
          <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto" />
          <h2 className="text-xl font-semibold text-slate-900">Account created!</h2>
          <p className="text-sm text-slate-500">Redirecting you to sign in…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="text-center mb-8">
          <img src="/favicon.svg" alt="DriveZone" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-white">DriveZone</h1>
          <p className="text-xs font-medium tracking-widest text-sky-100 uppercase mt-2">
            {refCode ? 'You were invited — create your account' : 'Create your account'}
          </p>
        </div>

        {refCode && (
          <div className="flex items-center gap-2.5 bg-white/15 border border-white/30
                          rounded-xl px-4 py-3 mb-4">
            <Gift className="w-4 h-4 text-white flex-shrink-0" />
            <p className="text-sm text-white font-medium">
              Referral code: <span className="font-semibold tracking-widest">{refCode}</span>
            </p>
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name */}
            <div className="relative">
              <input
                id="reg-firstname"
                type="text"
                placeholder=" "
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoFocus
                className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                           text-slate-900 text-sm
                           focus:outline-none focus:border-sky-500 focus:bg-white
                           transition-colors duration-150"
              />
              <label
                htmlFor="reg-firstname"
                className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                           transition-all duration-150
                           peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                           peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
              >
                First name
              </label>
            </div>

            {/* Email */}
            <div className="relative">
              <input
                id="reg-email"
                type="email"
                placeholder=" "
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                           text-slate-900 text-sm
                           focus:outline-none focus:border-sky-500 focus:bg-white
                           transition-colors duration-150"
              />
              <label
                htmlFor="reg-email"
                className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                           transition-all duration-150
                           peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                           peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
              >
                E-mail
              </label>
            </div>

            {/* Password */}
            <div className="relative">
              <input
                id="reg-password"
                type={showPw ? 'text' : 'password'}
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="peer w-full px-4 pt-6 pb-2 pr-12 rounded-lg border-2 border-slate-400 bg-slate-50
                           text-slate-900 text-sm
                           focus:outline-none focus:border-sky-500 focus:bg-white
                           transition-colors duration-150"
              />
              <label
                htmlFor="reg-password"
                className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                           transition-all duration-150
                           peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                           peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
              >
                Password
              </label>
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              {password && (
                <p className={`text-xs mt-1.5 ${passwordStrong ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {passwordStrong ? '✓ Password looks good' : 'At least 8 characters + 1 number'}
                </p>
              )}
            </div>

            {/* Confirm */}
            <div className="relative">
              <input
                id="reg-confirm"
                type={showPw ? 'text' : 'password'}
                placeholder=" "
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                           text-slate-900 text-sm
                           focus:outline-none focus:border-sky-500 focus:bg-white
                           transition-colors duration-150"
              />
              <label
                htmlFor="reg-confirm"
                className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                           transition-all duration-150
                           peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                           peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
              >
                Confirm password
              </label>
              {confirm && (
                <p className={`text-xs mt-1.5 ${passwordsMatch ? 'text-emerald-600' : 'text-red-500'}`}>
                  {passwordsMatch ? '✓ Passwords match' : 'Passwords do not match'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !firstName || !email || !passwordStrong || !passwordsMatch}
              className="w-full py-3.5 rounded-lg font-semibold text-sm text-white
                         bg-sky-600 hover:bg-sky-700 active:bg-sky-800
                         transition-colors duration-150
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-sky-100 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-white font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
